/* eslint-disable camelcase */

import classNames from 'classnames';
import { useEffect, useState, useRef } from 'react';
import { isFeatureActive } from '@helpers';
import { useConfig } from '@common/Hooks/useConfig';
import MenuListItem from '@common/Menu/MenuListItem';
import { parseJSON } from '@helpers/data';
import { hasMultipleLanguages } from '@helpers/hasMultipleLanguages';
import { BurgerNavBreadcrumbs } from './BurgerNavBreadcrumbs';
import BurgerNavDepartments from './BurgerNavDepartments';
import { BurgerNavSearch } from './BurgerNavSearch';
import { languageSelectId } from './constants/languageSelect';
import { storeSelectId } from './constants/storeSelect';
import { getCurrentNavigation } from './helpers';
import { useBurgerNavHandlers } from './hooks/useBurgerNavHandlers';
import { useClickHandlers } from './hooks/useClickHandlers';
import { useMenu } from './hooks/useMenu';
import { useNavigationStateHandler } from './hooks/useNavigationStateHandler';
import NavHeader from './NavHeader';
import './styles/BurgerNavContent.scss';

const defaultView = 'default';

function BurgerNavContent({
    setIsNavOpen,
    serviceSection,
    imagesSourceUrl,
    customerServicePageUuids,
    languages,
    menuItems,
    navigation,
    uuids,
    tabIds,
    unisexTabIds,
    breadcrumbs,
    setSelectedDepartment,
    selectedDepartment
}) {
    const config = useConfig();
    const csPageUuids = parseJSON(customerServicePageUuids) ?? {};
    const { pageUuid } = csPageUuids;
    const [navigationState, navItemClick, storeSelectClick, languageSelectClick] = useClickHandlers(navigation, uuids);
    const {
        currentNavItem,
        navigationPath,
        selectedNavItem,
        rootNavigation
    } = navigationState;
    const [currentNav, setCurrentNav] = useState(currentNavItem);
    const menuContainerRef = useRef();
    const isMobileNavRefresh = isFeatureActive('PLT-34079_mobile_nav_refresh_2023', config);
    const isNewHamburgerNavigationActive = isFeatureActive(
        'PLT-32188-use-button-group-in-main-header-mobile-menu',
        config
    );

    let languagesWithIcon;

    if (languages) {
        languagesWithIcon = Object.entries(languages).reduce((accumulator, currentValue) => {
            const store = currentValue[0];
            const storeLanguages = currentValue[1];

            const storeLanguagesWithIcon = storeLanguages.map((language) => {
                const flagCode = language.code === 'en' ? 'uk' : language.country;
                return (
                    { ...language, icon: `flag_${flagCode}` }
                );
            });
            accumulator[store] = storeLanguagesWithIcon;
            return accumulator;
        }, {});
    }

    const [
        navClicked, view, backButtonClick, handleStoreSelectClick,
        handleLanguageSelectClick, menuItemClick
    ] = useBurgerNavHandlers(
        pageUuid,
        currentNav,
        navigationPath,
        storeSelectClick,
        languageSelectClick,
        languagesWithIcon,
        navItemClick,
        menuContainerRef,
        setIsNavOpen,
        selectedDepartment,
        setSelectedDepartment
    );
    const menu = useMenu(csPageUuids, serviceSection, currentNav, navClicked, languagesWithIcon);
    const { navigationStateFromPath } = useNavigationStateHandler(navigation);
    const { id, lvl } = currentNav;
    const hasSearchBooztMobile = [0, 1].indexOf(lvl) > -1;
    const isSecondOrGreaterLevel = lvl >= 2;
    const isFirstOrGreaterLevel = lvl >= 1;
    const isCountrySelect = ['languageSelect', 'storeSelect'].includes(id);

    useEffect(() => {
        document.body.classList.add('fixed-for-overlay');
        return () => document.body.classList.remove('fixed-for-overlay');
    }, []);

    useEffect(() => {
        setCurrentNav(currentNavItem);
    }, [currentNavItem]);

    return (
        <div className={classNames('burger-nav-content', { 'burger-nav-content-v2': isMobileNavRefresh })}>
            <NavHeader
                backButtonClick={backButtonClick}
                hasMultipleLanguages={hasMultipleLanguages(languages ? languages[config.store] : false)}
                isCountrySelect={isCountrySelect}
                navigation={getCurrentNavigation(view, currentNav)}
                rootNavigation={rootNavigation}
                setIsNavOpen={setIsNavOpen}
                tabIds={tabIds}
                unisexTabIds={unisexTabIds}
                uuids={uuids}
                onLanguageSelectClick={handleLanguageSelectClick}
                onStoreSelectClick={handleStoreSelectClick}
            />
            <div
                ref={menuContainerRef}
                className={classNames('burger-nav-content__menu-container',
                    { 'burger-nav-content__menu-container--new-nav': isMobileNavRefresh })}
                id="menu_container"
            >
                {view === defaultView && (
                    <>
                        {hasSearchBooztMobile && !isMobileNavRefresh
                            && (
                                <BurgerNavSearch
                                    customClass="burger-nav-content__search"
                                    parentNavigationItem={currentNav}
                                    onSearchInputClick={setIsNavOpen}
                                />
                            )}
                        {!isCountrySelect
                            && (
                                <div className="burger-nav-content__menu-top">
                                    {isNewHamburgerNavigationActive && isFirstOrGreaterLevel ? (
                                        <BurgerNavDepartments
                                            menuContainerRef={menuContainerRef}
                                            menuItems={menuItems}
                                            navigationPath={navigationPath}
                                            navItemClick={navItemClick}
                                            setSelectedDepartment={setSelectedDepartment}
                                        />
                                    ) : null}
                                    {isNewHamburgerNavigationActive && isSecondOrGreaterLevel ? (
                                        <BurgerNavBreadcrumbs
                                            backButtonClick={backButtonClick}
                                            breadcrumbs={breadcrumbs}
                                            currentNavItem={currentNavItem}
                                            navigationPath={navigationPath}
                                            navigationStateFromPath={navigationStateFromPath}
                                        />
                                    ) : null}
                                </div>
                            )}
                        <MenuListItem
                            currentNavItem={currentNav}
                            imagesSourceUrl={imagesSourceUrl}
                            items={menu}
                            menuItemClick={menuItemClick}
                            selectedMenuItem={selectedNavItem}
                            theme="mod2"
                            type="mobile_nav"
                            urlPrefix={navClicked !== storeSelectId
                                && navClicked !== languageSelectId
                                ? App.Config.urlPrefix : undefined}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default BurgerNavContent;
